import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { submitPrescription, verifyPrescription, sendPrescriptionToLead, sendPrescriptionFormToDoctor, updatePrescription } from '../../services/prescriptionApi';
import toast from 'react-hot-toast';
import { ArrowLeft, Send, CheckCircle, XCircle, Loader2, FileText, ShieldCheck, ShieldCheck as ShieldIcon, MessageSquare, User, Phone, Mail, Pill, RefreshCw, ClipboardCheck, Clock, AlertCircle, SendHorizontal, TestTubes, Plus, Trash2, Search, ChevronDown, ChevronRight, Stethoscope, ClipboardList, Shield, Heart, Lightbulb, Calendar, Edit, Save, X } from 'lucide-react';
import { ALL_INVESTIGATIONS, getFilteredInvestigations, groupInvestigationsByCategory, FREQUENCY_OPTIONS, STRENGTH_OPTIONS, TYPE_OPTIONS, DURATION_OPTIONS, INSTRUCTIONS_OPTIONS, UNIT_OPTIONS } from '../../constants/prescriptionConstants';

const DURATION_UNITS = ['days', 'weeks', 'months', 'years'];

const parseStrength = (val) => {
  const raw = String(val || '').trim();
  if (!raw) return { number: '', unit: 'mg' };
  const match = raw.match(/^([\d.]+)\s*(.*)$/);
  if (match) return { number: match[1], unit: match[2] || 'mg' };
  return { number: '', unit: raw };
};

const parseDuration = (val) => {
  const raw = String(val || '').trim();
  if (!raw) return { number: '', unit: 'days' };
  const match = raw.match(/^([\d.]+)\s*(.*)$/);
  if (match) return { number: match[1], unit: match[2] || 'days' };
  return { number: '', unit: raw };
};

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_verification: 'bg-orange-50 text-orange-700 border-orange-200',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sent_to_lead: 'bg-blue-50 text-blue-700 border-blue-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200'
};

const STATUS_FLOW = ['draft', 'submitted', 'pending_verification', 'verified', 'sent_to_lead'];

const PrescriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [prescription, setPrescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approved, setApproved] = useState(true);
  const [actionType, setActionType] = useState('approve');
  const [appointmentId, setAppointmentId] = useState('');
  const [sendingFormToDoctor, setSendingFormToDoctor] = useState(false);
  const [editingSections, setEditingSections] = useState({
    clinical: false,
    items: false,
    investigations: false,
    advice: false
  });
  const [editedItems, setEditedItems] = useState([]);
  const [editedInvestigations, setEditedInvestigations] = useState([]);
  const [editedNotes, setEditedNotes] = useState('');
  const [editedAdvice, setEditedAdvice] = useState('');
  const [editedFollowUp, setEditedFollowUp] = useState('');
  const [editedChiefComplaints, setEditedChiefComplaints] = useState('');
  const [editedPastMedicalHistory, setEditedPastMedicalHistory] = useState('');
  const [editedProvisionalDiagnosis, setEditedProvisionalDiagnosis] = useState('');
  const [editedComorbidities, setEditedComorbidities] = useState('');
  const [canEditPrescription, setCanEditPrescription] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [customFrequencyItems, setCustomFrequencyItems] = useState({});
  const [customStrengthItems, setCustomStrengthItems] = useState({});
  const [customTypeItems, setCustomTypeItems] = useState({});
  const [customDurationItems, setCustomDurationItems] = useState({});
  const [customInstructionsItems, setCustomInstructionsItems] = useState({});
  const [editInvestigationSearch, setEditInvestigationSearch] = useState('');
  const [showEditInvestigationDropdown, setShowEditInvestigationDropdown] = useState(false);

  const DRAFT_KEY = `prescription_detail_draft_${id}`;

  const loadDraftFromLocal = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.warn('Failed to load prescription draft', e);
    }
    return null;
  };

  const saveDraftToLocal = (draftPayload) => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draftPayload));
    } catch (e) {
      console.warn('Failed to save prescription draft', e);
    }
  };

  const clearDraftFromLocal = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) {}
  };

  const isAnySectionEditing = Object.values(editingSections).some(Boolean);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isAnySectionEditing) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAnySectionEditing]);

  useEffect(() => {
    if (!isAnySectionEditing) return;
    saveDraftToLocal({
      editingSections,
      editedItems,
      editedInvestigations,
      editedNotes,
      editedAdvice,
      editedFollowUp,
      editedChiefComplaints,
      editedPastMedicalHistory,
      editedProvisionalDiagnosis,
      editedComorbidities,
      customTypeItems,
      customStrengthItems,
      customFrequencyItems,
      customDurationItems,
      customInstructionsItems,
      savedAt: Date.now()
    });
  }, [
    isAnySectionEditing,
    editingSections,
    editedItems,
    editedInvestigations,
    editedNotes,
    editedAdvice,
    editedFollowUp,
    editedChiefComplaints,
    editedPastMedicalHistory,
    editedProvisionalDiagnosis,
    editedComorbidities,
    customTypeItems,
    customStrengthItems,
    customFrequencyItems,
    customDurationItems,
    customInstructionsItems
  ]);

  useEffect(() => {
    if (location.state?.appointmentId) {
      setAppointmentId(location.state.appointmentId);
    }
  }, [location.state?.appointmentId]);

  useEffect(() => {
    fetchPrescription(true);
  }, [id]);

  const fetchPrescription = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const res = await api.get(`/prescriptions/${id}`);
      if (res.data?.success && res.data?.data) {
        const pres = res.data.data;
        setPrescription(pres);
        if (pres.appointmentId) {
          const aptId = pres.appointmentId?._id || pres.appointmentId;
          setAppointmentId(aptId);
        }

        // Restore unsaved draft if present in localStorage
        const draft = loadDraftFromLocal();
        if (draft && draft.editingSections && Object.values(draft.editingSections).some(Boolean)) {
          setEditingSections(draft.editingSections);
          if (Array.isArray(draft.editedItems)) setEditedItems(draft.editedItems);
          if (Array.isArray(draft.editedInvestigations)) setEditedInvestigations(draft.editedInvestigations);
          if (draft.editedNotes !== undefined) setEditedNotes(draft.editedNotes);
          if (draft.editedAdvice !== undefined) setEditedAdvice(draft.editedAdvice);
          if (draft.editedFollowUp !== undefined) setEditedFollowUp(draft.editedFollowUp);
          if (draft.editedChiefComplaints !== undefined) setEditedChiefComplaints(draft.editedChiefComplaints);
          if (draft.editedPastMedicalHistory !== undefined) setEditedPastMedicalHistory(draft.editedPastMedicalHistory);
          if (draft.editedProvisionalDiagnosis !== undefined) setEditedProvisionalDiagnosis(draft.editedProvisionalDiagnosis);
          if (draft.editedComorbidities !== undefined) setEditedComorbidities(draft.editedComorbidities);
          if (draft.customTypeItems) setCustomTypeItems(draft.customTypeItems);
          if (draft.customStrengthItems) setCustomStrengthItems(draft.customStrengthItems);
          if (draft.customFrequencyItems) setCustomFrequencyItems(draft.customFrequencyItems);
          if (draft.customDurationItems) setCustomDurationItems(draft.customDurationItems);
          if (draft.customInstructionsItems) setCustomInstructionsItems(draft.customInstructionsItems);
          toast('Restored unsaved draft changes', { icon: '📝' });
        }
      } else {
        if (showLoading) setPrescription(null);
      }
    } catch (error) {
      console.error('Failed to load prescription:', error);
      if (showLoading) setPrescription(null);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleGoBack = () => {
    const targetAptId = appointmentId || prescription?.appointmentId?._id || prescription?.appointmentId;
    if (targetAptId) {
      navigate(`/appointments/${targetAptId}?tab=confirm`);
    } else {
      navigate(-1);
    }
  };

  const canUserEdit = () => {
    if (!prescription) return false;
    const isAdmin = user?.role === 'admin';
    const isCC = user?.role === 'customer_care' || user?.role === 'cc_head';
    const isOwner = prescription.prescribedBy?._id?.toString() === user?._id?.toString();
    const isAssignedDoctor = user?.role === 'doctor' && (
      prescription.doctorId?._id?.toString() === user?._id?.toString() ||
      prescription.doctorId?.user?._id?.toString() === user?._id?.toString() ||
      prescription.doctorId?.user?.toString() === user?._id?.toString()
    );
    if (isAdmin || isCC) return true;
    return (isOwner || isAssignedDoctor) && ['draft', 'submitted', 'pending_verification', 'rejected', 'sent_to_lead'].includes(prescription.status);
  };

  const isEditingSection = (sectionKey) => Boolean(editingSections[sectionKey]);

  const initEditData = () => {
    setEditedItems(Array.isArray(prescription?.prescriptionItems) ? JSON.parse(JSON.stringify(prescription.prescriptionItems)) : []);
    setEditedInvestigations(Array.isArray(prescription?.investigations) ? JSON.parse(JSON.stringify(prescription.investigations)) : []);
    setEditedNotes(prescription?.notes || '');
    setEditedAdvice(prescription?.advice || '');
    setEditedFollowUp(prescription?.followUp || '');
    setEditedChiefComplaints(prescription?.chiefComplaints || '');
    setEditedPastMedicalHistory(prescription?.pastMedicalHistory || '');
    setEditedProvisionalDiagnosis(prescription?.provisionalDiagnosis || '');
    setEditedComorbidities(prescription?.leadId?.comorbidities || '');
  };

  const startEditingSection = (sectionKey) => {
    initEditData();
    setEditingSections(prev => ({ ...prev, [sectionKey]: true }));
  };

  const cancelEditingSection = (sectionKey) => {
    const updated = { ...editingSections, [sectionKey]: false };
    setEditingSections(updated);
    if (!Object.values(updated).some(Boolean)) {
      clearDraftFromLocal();
    }
  };

  const saveSection = async (sectionKey) => {
    setEditLoading(true);
    try {
      const res = await updatePrescription(id, {
        chiefComplaints: editedChiefComplaints,
        pastMedicalHistory: editedPastMedicalHistory,
        provisionalDiagnosis: editedProvisionalDiagnosis,
        comorbidities: editedComorbidities,
        prescriptionItems: editedItems,
        investigations: editedInvestigations,
        notes: editedNotes,
        advice: editedAdvice,
        followUp: editedFollowUp
      });

      if (res?.data?.data) {
        setPrescription(res.data.data);
      }
      toast.success('Prescription section updated successfully');
      const updated = { ...editingSections, [sectionKey]: false };
      setEditingSections(updated);
      if (!Object.values(updated).some(Boolean)) {
        clearDraftFromLocal();
      }
      await fetchPrescription(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update section');
    } finally {
      setEditLoading(false);
    }
  };

  const addItem = () => {
    setEditedItems(prev => [...prev, { medicineName: '', type: '', strength: '500 mg', frequency: '', duration: '3 days', instructions: '', isCritical: false }]);
  };

  const removeItem = (index) => {
    setEditedItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setEditedItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const capitalizeFirst = (val) => {
    if (!val || typeof val !== 'string') return val;
    return val.charAt(0).toUpperCase() + val.slice(1);
  };

  const addInvestigation = (investigation) => {
    if (investigation) {
      const normalized = { ...investigation, label: capitalizeFirst(investigation.label) };
      setEditedInvestigations(prev => [...prev, normalized]);
    } else {
      setEditedInvestigations(prev => [...prev, { name: '', shortForm: '' }]);
    }
    setEditInvestigationSearch('');
    setShowEditInvestigationDropdown(false);
  };

  const removeInvestigation = (index) => {
    setEditedInvestigations(prev => prev.filter((_, i) => i !== index));
  };

  const updateInvestigation = (index, field, value) => {
    setEditedInvestigations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  useEffect(() => {
    setCanEditPrescription(canUserEdit());
  }, [prescription, user]);

  const handleSubmit = async () => {
    setActionLoading(true);
    try {
      await submitPrescription(id, { notes: actionNotes });
      toast.success('Prescription submitted for verification');
      setShowVerifyModal(false);
      setActionNotes('');
      fetchPrescription();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit prescription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async () => {
    setActionLoading(true);
    try {
      await verifyPrescription(id, {
        approved,
        verificationNotes: actionNotes,
        rejectionReason: !approved ? rejectionReason : undefined
      });
      toast.success(`Prescription ${approved ? 'verified' : 'rejected'} successfully`);
      setShowVerifyModal(false);
      setShowRejectModal(false);
      setActionNotes('');
      setRejectionReason('');
      setApproved(true);
      fetchPrescription();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify prescription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendToLead = async () => {
    setActionLoading(true);
    try {
      await sendPrescriptionToLead(id, { notes: actionNotes });
      toast.success('Prescription sent to lead successfully');
      setShowSendModal(false);
      setActionNotes('');
      fetchPrescription();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send prescription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDirectApproveAndSend = async () => {
    setActionLoading(true);
    try {
      if (prescription.status !== 'verified' && prescription.status !== 'sent_to_lead') {
        await verifyPrescription(id, {
          approved: true,
          verificationNotes: 'Approved & Sent directly by CC'
        });
      }
      await sendPrescriptionToLead(id, { notes: '' });
      const isResend = Boolean(prescription.sentToLeadAt || prescription.statusHistory?.some(h => h.status === 'sent_to_lead'));
      toast.success(isResend ? 'Updated prescription approved and resent to lead successfully' : 'Prescription approved and sent to lead successfully');
      fetchPrescription(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve and send prescription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendToLead = async () => {
    setActionLoading(true);
    try {
      await sendPrescriptionToLead(id, { notes: 'Resent to lead by CC' });
      toast.success('Prescription resent to lead successfully');
      fetchPrescription(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend prescription');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusStepIndex = (status) => {
    if (!status) return -1;
    return STATUS_FLOW.indexOf(status);
  };

  const isStepCompleted = (step) => {
    if (!prescription) return false;
    const currentIndex = getStatusStepIndex(prescription.status);
    const stepIndex = getStatusStepIndex(step);
    if (currentIndex === -1 || stepIndex === -1) return false;
    return stepIndex < currentIndex || (stepIndex === currentIndex && prescription.status === step);
  };

  const isCurrentStep = (step) => {
    return prescription?.status === step;
  };

  const canUserApproveAndSend = () => {
    if (!user || !prescription) return false;
    const isAdmin = user.role === 'admin';
    const isCC = user.role === 'customer_care' || user.role === 'cc_head';
    return (isAdmin || isCC) && prescription.status !== 'sent_to_lead';
  };

  const canUserResendToLead = () => {
    if (!user || !prescription) return false;
    const isAdmin = user.role === 'admin';
    const isCC = user.role === 'customer_care' || user.role === 'cc_head';
    return (isAdmin || isCC) && prescription.status === 'sent_to_lead';
  };

  const canUserVerify = () => {
    if (!user || !prescription) return false;
    const isAdmin = user.role === 'admin';
    const isCC = user.role === 'customer_care' || user.role === 'cc_head';
    return (isAdmin || isCC) && prescription.status !== 'sent_to_lead';
  };

  const canUserSendToLead = () => {
    if (!user || !prescription) return false;
    const isAdmin = user.role === 'admin';
    const isCC = user.role === 'customer_care' || user.role === 'cc_head';
    return (isAdmin || isCC) && prescription.status !== 'sent_to_lead';
  };

  const canUserSubmit = () => {
    if (!user || !prescription) return false;
    return prescription.prescribedBy?._id === user._id && prescription.status === 'draft';
  };

  const canUserSendFormToDoctor = () => {
    if (!user || !prescription) return false;
    const isAdmin = user.role === 'admin';
    const isCC = user.role === 'customer_care' || user.role === 'cc_head';
    return (isAdmin || isCC) && prescription.status !== 'sent_to_lead';
  };

  const handleSendFormToDoctor = async () => {
    if (!prescription) return;
    setSendingFormToDoctor(true);
    try {
      await sendPrescriptionFormToDoctor(prescription._id);
      toast.success('Prescription form sent to doctor successfully');
      fetchPrescription();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send prescription form to doctor');
    } finally {
      setSendingFormToDoctor(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#143E78] mx-auto"></div>
          <p className="text-sm font-bold text-slate-500 mt-3">Loading prescription...</p>
        </div>
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8 max-w-md mx-4">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-lg font-black text-slate-900 mb-2">Prescription not found</h3>
          <p className="text-sm text-slate-500 mb-6">The prescription you are looking for does not exist or has been removed.</p>
          <button
            onClick={() => navigate('/prescriptions')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all shadow-lg shadow-[#143E78]/20"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Prescriptions
          </button>
        </div>
      </div>
    );
  }

  const patientName = prescription.leadId?.patientName || '-';
  const patientAge = prescription.leadId?.patientAge || '-';
  const patientGender = prescription.leadId?.patientGender ? prescription.leadId.patientGender.charAt(0).toUpperCase() + prescription.leadId.patientGender.slice(1) : '-';
  const doctorName = prescription.doctorId?.user?.firstName && prescription.doctorId.user.lastName
    ? `${prescription.doctorId.user.firstName} ${prescription.doctorId.user.lastName}`
    : '-';
  const doctorRegNo = prescription.doctorId?.registrationNumber || '-';
  const doctorSpec = prescription.doctorId?.specialization || '-';

  const prescribedByName = prescription.prescribedBy?.firstName && prescription.prescribedBy.lastName
    ? `${prescription.prescribedBy.firstName} ${prescription.prescribedBy.lastName}`
    : '-';

  const notes = prescription.notes || '';
  const advice = prescription.advice || '';
  const followUp = prescription.followUp || '';
  const verificationNotes = prescription.verificationNotes || '';
  const rejectionReasonText = prescription.rejectionReason || '';
  const statusHistory = Array.isArray(prescription.statusHistory) ? prescription.statusHistory : [];
  const prescriptionItems = Array.isArray(prescription.prescriptionItems) ? prescription.prescriptionItems : [];
  const investigations = Array.isArray(prescription.investigations) ? prescription.investigations : [];
  const totalItems = prescription.totalItems || prescriptionItems.length;
  const editFilteredInvestigations = getFilteredInvestigations(editInvestigationSearch);
  const editGroupedInvestigations = groupInvestigationsByCategory(editFilteredInvestigations);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="w-full max-w-[98%] xl:max-w-[96%] mx-auto px-3 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#143E78] transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Appointment
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          <div className="px-6 sm:px-8 py-6 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-100">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h1 className="text-xl font-black text-slate-900 tracking-tight">
                       Prescription
                     </h1>
                    <p className="text-xs font-bold text-slate-500 mt-0.5">
                      Created {formatDate(prescription.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
              <span className={`inline-flex items-center px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border w-fit ${STATUS_COLORS[prescription.status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                {String(prescription.status || '').replace(/_/g, ' ')}
              </span>
            </div>
          </div>

          <div className="px-6 sm:px-8 py-5 border-b border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Status Progress</h3>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                {STATUS_FLOW.indexOf(prescription.status) >= 0 ? `${STATUS_FLOW.indexOf(prescription.status) + 1} of ${STATUS_FLOW.length}` : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              {STATUS_FLOW.map((step, index) => {
                const completed = isStepCompleted(step);
                const current = isCurrentStep(step);
                return (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        completed ? 'bg-[#143E78] text-white' :
                        current ? 'bg-[#143E78]/10 text-[#143E78] border-2 border-[#143E78]' :
                        'bg-slate-100 text-slate-400'
                      }`}>
                        {completed ? <CheckCircle className="w-4 h-4" /> : index + 1}
                      </div>
                      <span className={`text-[10px] mt-1.5 font-black uppercase tracking-wider ${current ? 'text-[#143E78]' : 'text-slate-400'}`}>
                        {step.replace(/_/g, ' ')}
                      </span>
                    </div>
                    {index < STATUS_FLOW.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 ${completed ? 'bg-[#143E78]' : 'bg-slate-200'}`}></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-slate-50/80 border-2 border-slate-100 rounded-2xl p-5">
                <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#143E78]" /> Patient Information
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Patient Name</p>
                    <p className="text-sm font-black text-slate-900">{patientName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Age</p>
                    <p className="text-sm font-bold text-slate-700">{patientAge} yrs</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Gender</p>
                    <p className="text-sm font-bold text-slate-700">{patientGender}</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50/80 border-2 border-slate-100 rounded-2xl p-5">
                <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-[#143E78]" /> Doctor Information
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Doctor Name</p>
                    <p className="text-sm font-black text-slate-900">{doctorName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Registration No.</p>
                    <p className="text-sm font-bold text-slate-700">{doctorRegNo}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Specialization</p>
                    <p className="text-sm font-bold text-slate-700">{doctorSpec}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── 1. Clinical Findings & Patient History Card ──────────── */}
            <div className="bg-white border-2 border-slate-100 rounded-2xl p-5 space-y-4 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-[#143E78]" /> Diagnosis & Clinical Findings
                  </h3>
                  <p className="text-[10px] font-bold text-slate-400">Diagnosis, chief complaints, past history, and comorbidities</p>
                </div>
                {canEditPrescription && (
                  <div>
                    {isEditingSection('clinical') ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => cancelEditingSection('clinical')}
                          disabled={editLoading}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => saveSection('clinical')}
                          disabled={editLoading}
                          className="px-3.5 py-1.5 bg-[#143E78] hover:bg-[#0f2f5e] text-white rounded-xl text-xs font-black transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                        >
                          {editLoading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          <span>Save</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditingSection('clinical')}
                        className="px-3 py-1.5 bg-[#143E78]/5 hover:bg-[#143E78]/10 text-[#143E78] border border-[#143E78]/20 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer"
                      >
                        <Edit size={13} />
                        <span>Edit Section</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Provisional Diagnosis */}
                <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3.5 space-y-1.5 md:col-span-2">
                  <span className="text-[10px] font-black text-[#143E78] uppercase tracking-widest flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" /> Diagnosis / Health Condition
                  </span>
                  {isEditingSection('clinical') ? (
                    <input
                      type="text"
                      value={editedProvisionalDiagnosis}
                      onChange={(e) => setEditedProvisionalDiagnosis(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                      placeholder="e.g. Acute Bronchitis / Grade II Gallstones"
                    />
                  ) : (
                    <p className="text-sm font-black text-slate-900">{prescription?.provisionalDiagnosis || '-'}</p>
                  )}
                </div>

                {/* Chief Complaints */}
                <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-3.5 space-y-1.5">
                  <span className="text-[10px] font-black text-rose-600 uppercase tracking-widest flex items-center gap-1.5">
                    <Heart className="w-3.5 h-3.5" /> Current Symptoms / Complaints
                  </span>
                  {isEditingSection('clinical') ? (
                    <textarea
                      value={editedChiefComplaints}
                      onChange={(e) => setEditedChiefComplaints(e.target.value)}
                      rows={3}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                      placeholder="Enter current symptoms and complaints..."
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {prescription?.chiefComplaints ? (
                        prescription.chiefComplaints.split(',').filter(Boolean).map((complaint, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1.5 rounded-xl bg-rose-50 border border-rose-200 text-xs font-black text-rose-700">
                            {complaint.trim()}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs font-bold text-slate-400 italic">No complaints reported</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Past Medical History */}
                <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-3.5 space-y-1.5">
                  <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-1.5">
                    <ClipboardList className="w-3.5 h-3.5" /> Past Medical & Surgical History
                  </span>
                  {isEditingSection('clinical') ? (
                    <textarea
                      value={editedPastMedicalHistory}
                      onChange={(e) => setEditedPastMedicalHistory(e.target.value)}
                      rows={3}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                      placeholder="Enter past illnesses, surgeries, history..."
                    />
                  ) : (
                    <p className="text-xs font-bold text-slate-800 whitespace-pre-wrap">{prescription?.pastMedicalHistory || '-'}</p>
                  )}
                </div>

                {/* Comorbidities */}
                {(isEditingSection('clinical') || prescription?.leadId?.comorbidities) && (
                  <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-3.5 space-y-1.5 md:col-span-2">
                    <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" /> Other Health Conditions / Allergies
                    </span>
                    {isEditingSection('clinical') ? (
                      <textarea
                        value={editedComorbidities}
                        onChange={(e) => setEditedComorbidities(e.target.value)}
                        rows={2}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                        placeholder="e.g. Asthma, BP, Diabetes, Penicillin Allergy..."
                      />
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {prescription?.leadId?.comorbidities?.split(',').filter(Boolean).map((item, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-xs font-black text-amber-800">
                            {item.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ─── 2. Prescribed Medications Table Card ─────────────────── */}
            <div className="bg-white border-2 border-slate-100 rounded-2xl p-5 space-y-4 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <Pill className="w-4 h-4 text-[#143E78]" /> Prescribed Medications
                  </h3>
                  <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-600 rounded-lg text-[10px] font-black">
                    {(isEditingSection('items') ? editedItems : prescriptionItems).length} item(s)
                  </span>
                </div>
                {canEditPrescription && (
                  <div>
                    {isEditingSection('items') ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={addItem}
                          className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-black transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <Plus size={13} />
                          <span>Add Medicine</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelEditingSection('items')}
                          disabled={editLoading}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => saveSection('items')}
                          disabled={editLoading}
                          className="px-3.5 py-1.5 bg-[#143E78] hover:bg-[#0f2f5e] text-white rounded-xl text-xs font-black transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                        >
                          {editLoading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          <span>Save</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditingSection('items')}
                        className="px-3 py-1.5 bg-[#143E78]/5 hover:bg-[#143E78]/10 text-[#143E78] border border-[#143E78]/20 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer"
                      >
                        <Edit size={13} />
                        <span>Edit Medicines</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="border border-slate-200 rounded-2xl overflow-x-auto shadow-2xs">
                <table className="w-full border-collapse text-left">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                      <th className="px-2.5 py-3 text-center w-8">#</th>
                      <th className="px-2.5 py-3 w-28">Type</th>
                      <th className="px-2.5 py-3 min-w-[160px]">Medicine Name</th>
                      <th className="px-2.5 py-3 w-36">Strength & Unit</th>
                      <th className="px-2.5 py-3 w-40">Frequency</th>
                      <th className="px-2.5 py-3 w-36">Duration</th>
                      <th className="px-2.5 py-3 w-40">Instructions</th>
                      {isEditingSection('items') && <th className="px-2.5 py-3 text-center w-12">Action</th>}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {(isEditingSection('items') ? editedItems : prescriptionItems).map((item, index) => (
                      <tr key={index} className="hover:bg-slate-50/70 transition-colors">
                        {isEditingSection('items') ? (
                          <>
                            <td className="px-2.5 py-2.5 text-center text-xs font-black text-slate-400">{index + 1}</td>
                            <td className="px-2.5 py-2.5">
                              {(() => {
                                const isCustomType = customTypeItems[index] || (item.type && !TYPE_OPTIONS.some((f) => f.value === item.type));
                                return !isCustomType ? (
                                  <select
                                    value={item.type || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === 'other') {
                                        setCustomTypeItems((prev) => ({ ...prev, [index]: true }));
                                        updateItem(index, 'type', '');
                                      } else {
                                        setCustomTypeItems((prev) => {
                                          const next = { ...prev };
                                          delete next[index];
                                          return next;
                                        });
                                        updateItem(index, 'type', val);
                                      }
                                    }}
                                    className="w-full h-8 px-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:border-[#143E78] outline-none cursor-pointer"
                                  >
                                    <option value="">Type</option>
                                    {TYPE_OPTIONS.filter((f) => f.value !== 'other').map((f) => (
                                      <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                    <option value="other">+ Custom</option>
                                  </select>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      value={item.type || ''}
                                      onChange={(e) => updateItem(index, 'type', e.target.value)}
                                      placeholder="Custom type"
                                      className="w-full h-8 px-2 bg-white border border-[#143E78] rounded-lg text-xs font-bold text-slate-900 outline-none"
                                      autoFocus
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setCustomTypeItems((prev) => {
                                          const next = { ...prev };
                                          delete next[index];
                                          return next;
                                        });
                                        updateItem(index, 'type', '');
                                      }}
                                      className="text-xs font-bold text-slate-400 hover:text-slate-600 px-1 cursor-pointer"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-2.5 py-2.5">
                              <input
                                type="text"
                                value={item.medicineName || ''}
                                onChange={(e) => updateItem(index, 'medicineName', e.target.value)}
                                placeholder="Medicine name..."
                                className="w-full h-8 px-2.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-2 focus:ring-[#143E78]/10 transition-all"
                              />
                            </td>
                            <td className="px-2.5 py-2.5">
                              <div className="flex items-center">
                                <input
                                  type="text"
                                  value={parseStrength(item.strength).number || ''}
                                  onChange={(e) => {
                                    const parsed = parseStrength(item.strength);
                                    const unit = item.unit || parsed.unit || 'mg';
                                    updateItem(index, 'strength', e.target.value ? `${e.target.value} ${unit}` : '');
                                  }}
                                  placeholder="500"
                                  className="w-14 h-8 px-1 text-center bg-white border border-slate-200 rounded-l-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] shrink-0"
                                />
                                {(() => {
                                  const isCustomUnit = customStrengthItems[index] || (item.unit && !UNIT_OPTIONS.some((f) => f.value === item.unit));
                                  return !isCustomUnit ? (
                                    <select
                                      value={item.unit || parseStrength(item.strength).unit || 'mg'}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === 'other') {
                                          setCustomStrengthItems((prev) => ({ ...prev, [index]: true }));
                                          updateItem(index, 'unit', '');
                                        } else {
                                          setCustomStrengthItems((prev) => {
                                            const next = { ...prev };
                                            delete next[index];
                                            return next;
                                          });
                                          const num = parseStrength(item.strength).number;
                                          updateItem(index, 'strength', num ? `${num} ${val}` : val);
                                          updateItem(index, 'unit', val);
                                        }
                                      }}
                                      className="w-20 h-8 px-1.5 bg-slate-50 border border-l-0 border-slate-200 rounded-r-lg text-xs font-bold text-slate-800 outline-none focus:border-[#143E78] shrink-0 cursor-pointer"
                                    >
                                      {UNIT_OPTIONS.filter((f) => f.value !== 'other').map((u) => (
                                        <option key={u.value} value={u.value}>{u.label}</option>
                                      ))}
                                      <option value="other">+ Custom</option>
                                    </select>
                                  ) : (
                                    <div className="flex items-center w-20 h-8 bg-white border border-l-0 border-[#143E78] rounded-r-lg px-1 shrink-0">
                                      <input
                                        type="text"
                                        value={item.unit || ''}
                                        onChange={(e) => {
                                          updateItem(index, 'unit', e.target.value);
                                          const num = parseStrength(item.strength).number;
                                          if (num) updateItem(index, 'strength', `${num} ${e.target.value}`);
                                        }}
                                        placeholder="unit"
                                        className="w-full text-xs font-bold text-slate-900 outline-none"
                                        autoFocus
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setCustomStrengthItems((prev) => {
                                            const next = { ...prev };
                                            delete next[index];
                                            return next;
                                          });
                                          updateItem(index, 'unit', 'mg');
                                        }}
                                        className="text-xs font-bold text-slate-400 hover:text-slate-600 px-0.5 cursor-pointer"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-2.5 py-2.5">
                              {(() => {
                                const isCustomFreq = customFrequencyItems[index] || (item.frequency && !FREQUENCY_OPTIONS.some((f) => f.value === item.frequency));
                                return !isCustomFreq ? (
                                  <select
                                    value={item.frequency || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === 'other') {
                                        setCustomFrequencyItems((prev) => ({ ...prev, [index]: true }));
                                        updateItem(index, 'frequency', '');
                                      } else {
                                        setCustomFrequencyItems((prev) => {
                                          const next = { ...prev };
                                          delete next[index];
                                          return next;
                                        });
                                        updateItem(index, 'frequency', val);
                                      }
                                    }}
                                    className="w-full h-8 px-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:border-[#143E78] outline-none cursor-pointer"
                                  >
                                    <option value="">Frequency</option>
                                    {FREQUENCY_OPTIONS.filter((f) => f.value !== 'other').map((f) => (
                                      <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                    <option value="other">+ Custom</option>
                                  </select>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      value={item.frequency || ''}
                                      onChange={(e) => updateItem(index, 'frequency', e.target.value)}
                                      placeholder="Custom frequency"
                                      className="w-full h-8 px-2 bg-white border border-[#143E78] rounded-lg text-xs font-bold text-slate-900 outline-none"
                                      autoFocus
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setCustomFrequencyItems((prev) => {
                                          const next = { ...prev };
                                          delete next[index];
                                          return next;
                                        });
                                        updateItem(index, 'frequency', '');
                                      }}
                                      className="text-xs font-bold text-slate-400 hover:text-slate-600 px-1 cursor-pointer"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-2.5 py-2.5">
                              <div className="flex items-center">
                                <input
                                  type="number"
                                  min="0"
                                  value={parseDuration(item.duration).number || ''}
                                  onChange={(e) => {
                                    const parsed = parseDuration(item.duration);
                                    const unit = item.durationUnit || parsed.unit || 'days';
                                    updateItem(index, 'duration', e.target.value ? `${e.target.value} ${unit}` : '');
                                  }}
                                  placeholder="3"
                                  className="w-14 h-8 px-1 text-center bg-white border border-slate-200 rounded-l-lg text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] shrink-0"
                                />
                                {(() => {
                                  const isCustomDur = customDurationItems[index] || (item.durationUnit && !DURATION_UNITS.includes(item.durationUnit));
                                  return !isCustomDur ? (
                                    <select
                                      value={item.durationUnit || parseDuration(item.duration).unit || 'days'}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === 'other') {
                                          setCustomDurationItems((prev) => ({ ...prev, [index]: true }));
                                          updateItem(index, 'durationUnit', '');
                                        } else {
                                          setCustomDurationItems((prev) => {
                                            const next = { ...prev };
                                            delete next[index];
                                            return next;
                                          });
                                          const num = parseDuration(item.duration).number;
                                          updateItem(index, 'durationUnit', val);
                                          if (num) updateItem(index, 'duration', `${num} ${val}`);
                                        }
                                      }}
                                      className="w-20 h-8 px-1.5 bg-slate-50 border border-l-0 border-slate-200 rounded-r-lg text-xs font-bold text-slate-800 outline-none focus:border-[#143E78] shrink-0 cursor-pointer"
                                    >
                                      {DURATION_UNITS.map((u) => (
                                        <option key={u} value={u}>{u}</option>
                                      ))}
                                      <option value="other">+ Custom</option>
                                    </select>
                                  ) : (
                                    <div className="flex items-center w-20 h-8 bg-white border border-l-0 border-[#143E78] rounded-r-lg px-1 shrink-0">
                                      <input
                                        type="text"
                                        value={item.durationUnit || ''}
                                        onChange={(e) => {
                                          updateItem(index, 'durationUnit', e.target.value);
                                          const num = parseDuration(item.duration).number;
                                          if (num) updateItem(index, 'duration', `${num} ${e.target.value}`);
                                        }}
                                        placeholder="unit"
                                        className="w-full text-xs font-bold text-slate-900 outline-none"
                                        autoFocus
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setCustomDurationItems((prev) => {
                                            const next = { ...prev };
                                            delete next[index];
                                            return next;
                                          });
                                          updateItem(index, 'durationUnit', 'days');
                                        }}
                                        className="text-xs font-bold text-slate-400 hover:text-slate-600 px-0.5 cursor-pointer"
                                        title="Back to dropdown"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-2.5 py-2.5">
                              {(() => {
                                const isCustomInstr = customInstructionsItems[index] || (item.instructions && !INSTRUCTIONS_OPTIONS.some((f) => f.value === item.instructions));
                                return !isCustomInstr ? (
                                  <select
                                    value={item.instructions || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === 'other') {
                                        setCustomInstructionsItems((prev) => ({ ...prev, [index]: true }));
                                        updateItem(index, 'instructions', '');
                                      } else {
                                        setCustomInstructionsItems((prev) => {
                                          const next = { ...prev };
                                          delete next[index];
                                          return next;
                                        });
                                        updateItem(index, 'instructions', val);
                                      }
                                    }}
                                    className="w-full h-8 px-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:border-[#143E78] outline-none cursor-pointer"
                                  >
                                    <option value="">Instructions</option>
                                    {INSTRUCTIONS_OPTIONS.filter((f) => f.value !== 'other').map((f) => (
                                      <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                    <option value="other">+ Custom</option>
                                  </select>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      value={item.instructions || ''}
                                      onChange={(e) => updateItem(index, 'instructions', e.target.value)}
                                      placeholder="Custom instruction"
                                      className="w-full h-8 px-2 bg-white border border-[#143E78] rounded-lg text-xs font-bold text-slate-900 outline-none"
                                      autoFocus
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setCustomInstructionsItems((prev) => {
                                          const next = { ...prev };
                                          delete next[index];
                                          return next;
                                        });
                                        updateItem(index, 'instructions', '');
                                      }}
                                      className="text-xs font-bold text-slate-400 hover:text-slate-600 px-1 cursor-pointer"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-2.5 py-2.5 text-center">
                              <button
                                onClick={() => removeItem(index)}
                                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                title="Remove medicine"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-2.5 py-3 text-xs font-black text-slate-900 text-center">{index + 1}</td>
                            <td className="px-2.5 py-3 text-xs font-bold text-slate-700 capitalize">{item.type ? item.type.replace(/_/g, ' ') : '-'}</td>
                            <td className="px-2.5 py-3 text-xs font-black text-slate-900">{item.medicineName || '-'}</td>
                            <td className="px-2.5 py-3 text-xs font-bold text-slate-700">
                              {(() => {
                                const parsed = parseStrength(item.strength);
                                const num = parsed.number;
                                const unit = item.unit || parsed.unit;
                                if (!num && !unit) return '-';
                                return `${num || ''} ${unit || ''}`.trim();
                              })()}
                            </td>
                            <td className="px-2.5 py-3 text-xs font-bold text-slate-700">
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded-md font-bold">
                                {item.frequency ? item.frequency.replace(/_/g, ' ') : '-'}
                              </span>
                            </td>
                            <td className="px-2.5 py-3 text-xs font-bold text-slate-700">
                              {(() => {
                                const parsed = parseDuration(item.duration);
                                const num = parsed.number;
                                const unit = item.durationUnit || parsed.unit;
                                if (!num && !unit) return '-';
                                return `${num || ''} ${unit || ''}`.trim();
                              })()}
                            </td>
                            <td className="px-2.5 py-3 text-xs font-bold text-slate-700">{item.instructions ? item.instructions.replace(/_/g, ' ') : '-'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── 3. Diagnostic Investigations Card ──────────────────── */}
            <div className="bg-white border-2 border-slate-100 rounded-2xl p-5 space-y-4 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <TestTubes className="w-4 h-4 text-[#143E78]" /> Diagnostic Investigations & Tests
                  </h3>
                  <span className="px-2 py-0.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg text-[10px] font-black">
                    {(isEditingSection('investigations') ? editedInvestigations : investigations).length} test(s)
                  </span>
                </div>
                {canEditPrescription && (
                  <div>
                    {isEditingSection('investigations') ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => cancelEditingSection('investigations')}
                          disabled={editLoading}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => saveSection('investigations')}
                          disabled={editLoading}
                          className="px-3.5 py-1.5 bg-[#143E78] hover:bg-[#0f2f5e] text-white rounded-xl text-xs font-black transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                        >
                          {editLoading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          <span>Save</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditingSection('investigations')}
                        className="px-3 py-1.5 bg-[#143E78]/5 hover:bg-[#143E78]/10 text-[#143E78] border border-[#143E78]/20 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer"
                      >
                        <Edit size={13} />
                        <span>Edit Tests</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Tags Display */}
              <div className="flex flex-wrap gap-2">
                {(isEditingSection('investigations') ? editedInvestigations : investigations).map((inv, index) => (
                  <div key={inv.id || index} className="inline-flex items-center gap-2 px-3 py-2 bg-[#143E78]/5 border border-[#143E78]/20 rounded-xl shadow-2xs">
                    <span className="text-sm">{inv.icon || '📋'}</span>
                    <div>
                      <p className="text-xs font-black text-[#143E78]">{inv.label}</p>
                      <p className="text-[9px] font-bold text-slate-500 uppercase">{inv.category}</p>
                    </div>
                    {isEditingSection('investigations') && (
                      <button
                        type="button"
                        onClick={() => removeInvestigation(index)}
                        className="p-1 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors ml-1 cursor-pointer"
                        title="Remove test"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {!(isEditingSection('investigations') ? editedInvestigations : investigations).length && (
                  <p className="text-xs font-bold text-slate-400 italic">No investigations or tests requested</p>
                )}
              </div>

              {/* Edit Mode Search / Add Bar */}
              {isEditingSection('investigations') && (
                <div className="pt-2 relative">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="text"
                        value={editInvestigationSearch}
                        onChange={(e) => setEditInvestigationSearch(capitalizeFirst(e.target.value))}
                        onFocus={() => setShowEditInvestigationDropdown(true)}
                        onBlur={() => setTimeout(() => setShowEditInvestigationDropdown(false), 200)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const trimmed = editInvestigationSearch.trim();
                            if (!trimmed) return;
                            const exactMatch = editFilteredInvestigations.find(
                              (inv) => inv.label.toLowerCase() === trimmed.toLowerCase()
                            );
                            if (exactMatch) {
                              addInvestigation(exactMatch);
                            } else if (trimmed) {
                              addInvestigation({
                                id: `custom_${Date.now()}`,
                                label: trimmed,
                                category: 'Custom',
                                icon: '📋',
                                categoryKey: 'OTHER'
                              });
                            }
                          }
                        }}
                        placeholder="Search tests (e.g. CBC, Lipid Profile, USG) or type custom..."
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const trimmed = editInvestigationSearch.trim();
                        if (!trimmed) return;
                        const exactMatch = editFilteredInvestigations.find(
                          (inv) => inv.label.toLowerCase() === trimmed.toLowerCase()
                        );
                        if (exactMatch) {
                          addInvestigation(exactMatch);
                        } else if (trimmed) {
                          addInvestigation({
                            id: `custom_${Date.now()}`,
                            label: trimmed,
                            category: 'Custom',
                            icon: '📋',
                            categoryKey: 'OTHER'
                          });
                        }
                      }}
                      className="px-4 py-2.5 bg-[#143E78] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all cursor-pointer shrink-0"
                    >
                      + Add Test
                    </button>
                  </div>

                  {showEditInvestigationDropdown && editFilteredInvestigations.length > 0 && (
                    <div className="absolute z-30 w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-100">
                      {Object.entries(editGroupedInvestigations).map(([key, group]) => (
                        <div key={key}>
                          <div className="px-4 py-2 bg-slate-50 text-[10px] font-black text-slate-500 uppercase tracking-widest sticky top-0">
                            {group.icon} {group.label}
                          </div>
                          {group.items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { addInvestigation(item); setEditInvestigationSearch(''); }}
                              className="w-full flex items-center justify-between px-4 py-2 hover:bg-[#143E78]/5 transition-colors text-left cursor-pointer"
                            >
                              <span className="text-xs font-bold text-slate-900">{item.label}</span>
                              <span className="text-[10px] font-black text-[#143E78] bg-[#143E78]/10 px-2 py-0.5 rounded-lg">{item.category}</span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ─── 4. Advice, Follow-Up & Notes Card ──────────────────── */}
            <div className="bg-white border-2 border-slate-100 rounded-2xl p-5 space-y-4 shadow-xs">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-[#143E78]" /> Patient Advice, Follow-Up & Notes
                  </h3>
                  <p className="text-[10px] font-bold text-slate-400">Diet guidelines, follow-up schedule, and internal observations</p>
                </div>
                {canEditPrescription && (
                  <div>
                    {isEditingSection('advice') ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => cancelEditingSection('advice')}
                          disabled={editLoading}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => saveSection('advice')}
                          disabled={editLoading}
                          className="px-3.5 py-1.5 bg-[#143E78] hover:bg-[#0f2f5e] text-white rounded-xl text-xs font-black transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                        >
                          {editLoading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          <span>Save</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditingSection('advice')}
                        className="px-3 py-1.5 bg-[#143E78]/5 hover:bg-[#143E78]/10 text-[#143E78] border border-[#143E78]/20 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer"
                      >
                        <Edit size={13} />
                        <span>Edit Section</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Advice */}
                <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-3.5 space-y-1.5">
                  <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest flex items-center gap-1.5">
                    <Lightbulb className="w-3.5 h-3.5" /> Patient Diet & Lifestyle Advice
                  </span>
                  {isEditingSection('advice') ? (
                    <textarea
                      value={editedAdvice}
                      onChange={(e) => setEditedAdvice(e.target.value)}
                      rows={3}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                      placeholder="Enter diet guidelines, rest, or lifestyle advice for the patient..."
                    />
                  ) : (
                    <p className="text-xs font-bold text-slate-800 whitespace-pre-wrap">{advice || '-'}</p>
                  )}
                </div>

                {/* Follow-Up */}
                <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-3.5 space-y-1.5">
                  <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> Next Follow-Up / Visit
                  </span>
                  {isEditingSection('advice') ? (
                    <textarea
                      value={editedFollowUp}
                      onChange={(e) => setEditedFollowUp(e.target.value)}
                      rows={3}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                      placeholder="Enter follow-up instructions..."
                    />
                  ) : (
                    <p className="text-xs font-bold text-slate-800 whitespace-pre-wrap">{followUp || '-'}</p>
                  )}
                </div>

                {/* Internal Notes */}
                {(isEditingSection('advice') || notes) && (
                  <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-3.5 space-y-1.5 md:col-span-2">
                    <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5" /> Internal Notes / Clinical Remarks
                    </span>
                    {isEditingSection('advice') ? (
                      <textarea
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        rows={3}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
                        placeholder="Enter internal notes for clinical team..."
                      />
                    ) : (
                      <p className="text-xs font-bold text-slate-800 whitespace-pre-wrap">{notes || '-'}</p>
                    )}
                  </div>
                )}
              </div>
            </div>



             {verificationNotes && (
              <div className="bg-emerald-50/80 border-2 border-emerald-100 rounded-2xl p-5">
                <h4 className="text-[11px] font-black text-emerald-700 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <ShieldIcon className="w-4 h-4" /> Verification Notes
                </h4>
                <p className="text-sm font-bold text-emerald-800 leading-relaxed">{verificationNotes}</p>
              </div>
            )}

            {rejectionReasonText && (
              <div className="bg-rose-50/80 border-2 border-rose-100 rounded-2xl p-5">
                <h4 className="text-[11px] font-black text-rose-700 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <XCircle className="w-4 h-4" /> Rejection Reason
                </h4>
                <p className="text-sm font-bold text-rose-800 leading-relaxed">{rejectionReasonText}</p>
              </div>
            )}

            {statusHistory.length > 0 && (
              <div>
                <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-4">Status History</h3>
                <div className="bg-white border-2 border-slate-100 rounded-2xl p-5">
                  <div className="space-y-4">
                    {[...statusHistory].reverse().map((entry, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#143E78] mt-2 flex-shrink-0"></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-black text-slate-900 uppercase tracking-wider">{String(entry?.status || '').replace(/_/g, ' ')}</span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{formatDate(entry?.changedAt)}</span>
                          </div>
                          {entry?.notes && <p className="text-sm text-slate-600 leading-relaxed">{entry.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex flex-wrap gap-3">
            {canUserResendToLead() && (
              <button
                onClick={handleResendToLead}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 disabled:opacity-50 cursor-pointer"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizontal className="w-4 h-4" />}
                {actionLoading ? 'Resending...' : 'Resend to Lead'}
              </button>
            )}
            {canUserSubmit() && (
              <button
                onClick={() => { setActionType('submit'); setShowVerifyModal(true); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-amber-700 transition-all shadow-lg shadow-amber-200 cursor-pointer"
              >
                <Send className="w-4 h-4" /> Submit
              </button>
            )}
            {canUserApproveAndSend() && (
              <button
                onClick={handleDirectApproveAndSend}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 disabled:opacity-50 cursor-pointer"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizontal className="w-4 h-4" />}
                {actionLoading ? 'Processing...' : (Boolean(prescription.sentToLeadAt || prescription.statusHistory?.some(h => h.status === 'sent_to_lead')) ? 'Approve & Resend to Lead' : 'Approve & Send to Lead')}
              </button>
            )}
            {canUserVerify() && (
              <button
                onClick={() => { setActionType('reject'); setShowRejectModal(true); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-rose-700 transition-all shadow-lg shadow-rose-200 cursor-pointer"
              >
                <XCircle className="w-4 h-4" /> Reject
              </button>
            )}
            {canUserSendFormToDoctor() && (
              <button
                onClick={handleSendFormToDoctor}
                disabled={sendingFormToDoctor}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all shadow-lg shadow-[#143E78]/20 disabled:opacity-50 cursor-pointer"
                title={prescription.prescriptionFormSentAt ? `Form last sent: ${formatDate(prescription.prescriptionFormSentAt)}. Click to resend to doctor for correction.` : 'Send form link to doctor'}
              >
                {sendingFormToDoctor ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {sendingFormToDoctor ? 'Sending...' : (prescription.prescriptionFormSentAt ? 'Resend Form to Doctor' : 'Send Form to Doctor')}
              </button>
            )}
            <button
              onClick={handleGoBack}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Appointment
            </button>
          </div>
        </div>

        {showVerifyModal && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-[#0f172a]/40 backdrop-blur-sm" onClick={() => { setShowVerifyModal(false); setShowRejectModal(false); }} />
            <div className="relative bg-white rounded-[28px] border border-slate-100 shadow-[0_20px_80px_-20px_rgba(20,62,120,0.25)] w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-black text-[#143E78] tracking-tight">
                  {actionType === 'submit' ? 'Submit Prescription' : actionType === 'approve' ? 'Approve Prescription' : 'Reject Prescription'}
                </h3>
                <button onClick={() => { setShowVerifyModal(false); setShowRejectModal(false); setActionNotes(''); }} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-5">
                {actionType === 'reject' ? (
                  <div>
                    <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2">Rejection Reason *</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                      placeholder="Provide a reason for rejection..."
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2">Notes</label>
                    <textarea
                      value={actionNotes}
                      onChange={(e) => setActionNotes(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                      placeholder="Add notes..."
                    />
                  </div>
                )}
                <div className="flex gap-3 justify-end pt-2">
                  <button
                    onClick={() => { setShowVerifyModal(false); setShowRejectModal(false); setActionNotes(''); setRejectionReason(''); }}
                    className="px-5 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={['approve', 'reject'].includes(actionType) ? handleVerify : handleSubmit}
                    disabled={actionLoading || (actionType === 'reject' && !rejectionReason.trim())}
                    className="px-5 py-2.5 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all shadow-lg shadow-[#143E78]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {actionType === 'submit' ? 'Confirm' : actionType === 'approve' ? 'Approve' : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSendModal && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-[#0f172a]/40 backdrop-blur-sm" onClick={() => { setShowSendModal(false); setActionNotes(''); }} />
            <div className="relative bg-white rounded-[28px] border border-slate-100 shadow-[0_20px_80px_-20px_rgba(20,62,120,0.25)] w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-black text-[#143E78] tracking-tight">Send Prescription to Lead</h3>
                <button onClick={() => { setShowSendModal(false); setActionNotes(''); }} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm font-bold text-slate-500 mb-5">
                This will send the prescription to <span className="text-slate-900">{patientName}</span>. They will be notified.
              </p>
              <div className="space-y-5">
                <div>
                  <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2">Notes (optional)</label>
                  <textarea
                    value={actionNotes}
                    onChange={(e) => setActionNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                    placeholder="Add any notes for the lead..."
                  />
                </div>
                <div className="flex gap-3 justify-end pt-2">
                  <button
                    onClick={() => { setShowSendModal(false); setActionNotes(''); }}
                    className="px-5 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendToLead}
                    disabled={actionLoading}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Send to Lead
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrescriptionDetail;
